import React, { useMemo } from 'react';
import ClientSchemaProvider, { useClientSchemaContext } from '@/context/clientSchemaContext';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateContactMutation } from '@/features/contacts/contactsApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useGetFormConfigQuery } from '@/features/config/configApi';
import { Form, Formik } from 'formik';
import { difference } from '@/utils/constants';
import { showToast } from '@/features/toast/toastSlice';
import { FormikErrorScroll } from '@/hooks/useFormikHelper';
import {
	Button,
	CardActions,
	CircularProgress,
	DialogContent,
	Skeleton,
	useTheme,
} from '@mui/material';
import { EditContact } from '../CreateClientForm/Fields/ContactFields/Contact';
import DevJSONView from '@/components/DevJSONView';
import { isProspectFieldVisible, VIEW_TYPES } from '@/features/config/helper';

const GROUP = 'contact';

const UpdateContactFormWrapper = () => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const [updateContact, updateContactState] = useUpdateContactMutation();

	const {
		isProspect,
		view,
		initialValues,
		schema: validationSchema,
		isLoading,
	} = useClientSchemaContext();

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const fieldsQuery = useGetFormConfigQuery(
		{ configType: 'client' },
		{
			skip: !userDetails,
			selectFromResult: ({ data, ...rest }) => ({
				...rest,
				data:
					data?.filter(
						(f) =>
							f.group === GROUP &&
							(isProspect ? isProspectFieldVisible(f, view) : f.visible?.[view]),
					) ?? [],
			}),
		},
	);

	const fields = useMemo(() => {
		const data = (fieldsQuery.data ?? []).slice().map((f) => ({
			...f,
			required: f.mandatory?.[isProspect ? 'prospect' : 'client'],
		}));

		return data.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
	}, [fieldsQuery.data, isProspect]);

	return (
		<Formik
			{...{
				initialValues,
				validationSchema,
				validateOnBlur: true,
				enableReinitialize: true,
				onSubmit: async (values, { setSubmitting }) => {
					setSubmitting(true);
					console.log('[onSubmit] Submitting values', values);
					const { contacts } = values;

					try {
						const contact = contacts[0];
						const diffFields = difference(initialValues.contacts[0], contact);
						console.log('Diff fields', diffFields);
						if (Object.keys(diffFields).length > 0) {
							const {
								Contact,
								Name,
								BirthDate,
								Email,
								Telephone,
								Telephone2,
								JobTitle,
								Mobile,
								Fax,
								Salutation,
								isMainContact,
							} = diffFields;

							const contactRes = await updateContact({
								insightId: contact.Id,
								clientId: contact?.clientId,
								Name: Contact ?? Name,
								BirthDate,
								Email,
								Telephone,
								Telephone2,
								JobTitle,
								Mobile,
								Fax,
								Salutation,
								IsPrimary: isMainContact,
							}).unwrap();

							console.log('Update contact res', contactRes);
							dispatch(
								showToast({
									message: 'Updated contact!',
									success: true,
									autohide: true,
								}),
							);
						}
					} catch (e) {
						console.error('[onSubmit] Error adding contact', e);
						const message = e.data?.message ?? e?.message;
						dispatch(
							showToast({
								message: `Error adding contact: ${message}`,
								error: true,
							}),
						);
					} finally {
						setSubmitting(false);
					}
				},
			}}
		>
			{({ isSubmitting, values, handleSubmit, errors, validateForm }) => {
				const disabled = isSubmitting || updateContactState.isLoading;

				return (
					<Form
						style={{
							flex: 1,
							display: 'flex',
							flexDirection: 'column',
							minHeight: 0,
						}}
					>
						<FormikErrorScroll {...{ isSubmitting }} />
						<DialogContent sx={{ px: 0, flex: 1, overflowY: 'auto' }}>
							{isLoading || fieldsQuery.isLoading || fieldsQuery.isUninitialized ? (
								<Skeleton width='100%' height='3em' />
							) : (
								<EditContact {...{ fields, index: 0, disabled }} />
							)}
						</DialogContent>
						<CardActions
							sx={{
								position: 'sticky',
								zIndex: 1,
								backgroundColor: theme.palette.background.paper,
								borderTop: `1px solid ${theme.palette.divider}`,
								justifyContent: 'center',
							}}
						>
							<Button
								color='primary'
								variant='contained'
								disabled={disabled}
								onClick={async () => {
									handleSubmit();
									setTimeout(() => {
										validateForm();
									}, 100);
								}}
							>
								{disabled ? (
									<CircularProgress size='2em' sx={{ color: '#ffffff' }} />
								) : (
									'Update'
								)}
							</Button>
						</CardActions>

						<DevJSONView value={{ values, errors }} />
					</Form>
				);
			}}
		</Formik>
	);
};

const UpdateContactForm = ({ contactData }) => {
	return (
		<ClientSchemaProvider {...{ view: VIEW_TYPES.update, variant: 'editContact', contactData }}>
			<UpdateContactFormWrapper />
		</ClientSchemaProvider>
	);
};

export default UpdateContactForm;
